import React from "react";
import { Link, useParams } from "react-router-dom";

import {
  FaCheckCircle,
  FaTimesCircle,
  FaQuestionCircle,
} from "react-icons/fa";

import {
  useGetEnglishAttemptByIdQuery,
} from "../../slices/englishQuizAttemptApiSlice";


const EnglishQuizAttemptDetailsScreen = () => {
  const { id } = useParams();

  const {
    data: attempt,
    isLoading,
    error,
  } = useGetEnglishAttemptByIdQuery(id);


  // ======================================================
  // Loading
  // ======================================================

  if (isLoading) {
    return (
      <div className="p-10 text-center">
        Loading attempt details...
      </div>
    );
  }


  // ======================================================
  // Error
  // ======================================================

  if (error || !attempt) {
    return (
      <div className="p-10 text-center text-red-600">
        Unable to load attempt details.
      </div>
    );
  }


  // ======================================================
  // Render
  // ======================================================

  return (
    <div className="min-h-screen bg-gray-50 p-6">

      <div className="max-w-5xl mx-auto">

        {/* ==================================================
            Back
        ================================================== */}

        <Link
          to="/student/english-quiz-attempts"
          className="
            text-blue-600
            mb-5
            inline-block
          "
        >
          ← Back to My Attempts
        </Link>


        {/* ==================================================
            Header
        ================================================== */}

        <div
          className="
            rounded-2xl
            bg-white
            shadow
            p-6
          "
        >

          <h1 className="text-2xl font-bold text-gray-800">
            {attempt.quizName ||
              attempt.quiz?.name ||
              "English Quiz"}
          </h1>

          <p className="mt-1 text-gray-500">
            Attempt #{attempt.attemptNumber || 1}
          </p>


          {/* Score */}

          <div
            className="
              mt-6
              grid
              grid-cols-2
              md:grid-cols-4
              gap-4
            "
          >

            <div className="rounded-xl bg-blue-50 p-4">
              <p className="text-sm text-gray-500">
                Score
              </p>

              <p className="mt-1 text-xl font-bold text-blue-700">
                {attempt.obtainedMarks ?? 0}
                {" / "}
                {attempt.totalMarks ?? 0}
              </p>
            </div>


            <div className="rounded-xl bg-purple-50 p-4">
              <p className="text-sm text-gray-500">
                Percentage
              </p>

              <p className="mt-1 text-xl font-bold text-purple-700">
                {attempt.percentage ?? 0}%
              </p>
            </div>


            <div className="rounded-xl bg-green-50 p-4">
              <p className="text-sm text-gray-500">
                Correct
              </p>

              <p className="mt-1 text-xl font-bold text-green-700">
                {attempt.correctAnswers ?? 0}
              </p>
            </div>


            <div className="rounded-xl bg-red-50 p-4">
              <p className="text-sm text-gray-500">
                Wrong
              </p>

              <p className="mt-1 text-xl font-bold text-red-700">
                {attempt.wrongAnswers ?? 0}
              </p>
            </div>

          </div>

        </div>


        {/* ==================================================
            Questions
        ================================================== */}

        <div className="mt-6 space-y-5">

          {(attempt.questions || []).map(
            (question, index) => {

              const answer =
                (attempt.answers || []).find(
                  (item) =>
                    item.question?.toString() ===
                    question._id?.toString(),
                );

              const isCorrect =
                answer?.isCorrect === true;

              const isUnanswered =
                answer?.selectedAnswer === null ||
                answer?.selectedAnswer === undefined ||
                answer?.selectedAnswer === "" ||
                (
                  Array.isArray(
                    answer?.selectedAnswer,
                  ) &&
                  answer.selectedAnswer.length === 0
                );


              return (
                <div
                  key={question._id}
                  className="
                    rounded-2xl
                    bg-white
                    shadow
                    p-6
                  "
                >

                  {/* Question */}

                  <div className="flex gap-3">

                    <span
                      className="
                        flex
                        h-8
                        w-8
                        shrink-0
                        items-center
                        justify-center
                        rounded-full
                        bg-blue-100
                        text-sm
                        font-bold
                        text-blue-700
                      "
                    >
                      {index + 1}
                    </span>


                    <div className="flex-1">

                      <p className="text-lg font-semibold text-gray-800 whitespace-pre-line">
                        {question.questionText}
                      </p>


                      {/* Image */}

                      {question.imageUrl && (
                        <img
                          src={question.imageUrl}
                          alt="Question"
                          className="
                            mt-4
                            max-w-full
                            rounded-xl
                          "
                        />
                      )}


                      {/* Options */}

                      {question.options?.length > 0 && (
                        <div className="mt-4 space-y-2">

                          {question.options.map(
                            (option) => {

                              const selected =
                                Array.isArray(
                                  answer?.selectedAnswer,
                                )
                                  ? answer.selectedAnswer.includes(
                                      option.key,
                                    )
                                  : answer?.selectedAnswer ===
                                    option.key;

                              const correct =
                                Array.isArray(
                                  answer?.correctAnswer,
                                )
                                  ? answer.correctAnswer.includes(
                                      option.key,
                                    )
                                  : answer?.correctAnswer ===
                                    option.key;

                              return (
                                <div
                                  key={option.key}
                                  className={`
                                    rounded-xl
                                    border
                                    p-3
                                    ${
                                      correct
                                        ? "border-green-400 bg-green-50"
                                        : selected
                                          ? "border-red-400 bg-red-50"
                                          : "border-gray-200"
                                    }
                                  `}
                                >

                                  <span className="font-semibold">
                                    {option.key}.
                                  </span>{" "}

                                  {option.text}

                                  {correct && (
                                    <span className="ml-2 text-green-600 text-sm font-semibold">
                                      Correct Answer
                                    </span>
                                  )}

                                  {selected &&
                                    !correct && (
                                      <span className="ml-2 text-red-600 text-sm font-semibold">
                                        Your Answer
                                      </span>
                                    )}

                                </div>
                              );
                            },
                          )}

                        </div>
                      )}


                      {/* Answer Status */}

                      <div className="mt-5">

                        {isUnanswered ? (
                          <div className="flex items-center gap-2 text-yellow-600">

                            <FaQuestionCircle />

                            <span className="font-semibold">
                              Not Answered
                            </span>

                          </div>
                        ) : isCorrect ? (
                          <div className="flex items-center gap-2 text-green-600">

                            <FaCheckCircle />

                            <span className="font-semibold">
                              Correct
                            </span>

                          </div>
                        ) : (
                          <div className="flex items-center gap-2 text-red-600">

                            <FaTimesCircle />

                            <span className="font-semibold">
                              Incorrect
                            </span>

                          </div>
                        )}

                      </div>


                      {/* Explanation */}

                      {question.explanation && (
                        <div
                          className="
                            mt-4
                            rounded-xl
                            bg-blue-50
                            p-4
                          "
                        >

                          <p className="font-semibold text-blue-800">
                            Explanation
                          </p>

                          <p className="mt-1 text-gray-700 whitespace-pre-line">
                            {question.explanation}
                          </p>

                        </div>
                      )}

                    </div>

                  </div>

                </div>
              );
            },
          )}

        </div>

      </div>

    </div>
  );
};


export default EnglishQuizAttemptDetailsScreen;